import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * sessionStorage: a back-in-stock form sent the customer form; read on the page the full-page post
 * returns to. Value: `{ formId: string, variantId: string, tags: string, time: number }`.
 *
 * Shared contract: `newsletter-popup.js` reads this key (and the `BackInStockForm-` form id prefix)
 * to tell a restock signup from its own, so it never shows its discount panel for one. It only
 * reads; this module removes the marker. Keep the key, shape and prefix in sync with that file.
 */
const PENDING_KEY = 'back-in-stock:pending';
/** A pending submission expires after this, which leaves time for a bot-protection challenge. */
const PENDING_MAX_AGE_MS = 30 * 60 * 1000;
/**
 * The newsletter popup's own marker (`newsletter-popup.js`). When the URL has no hash, the newer of
 * the two markers names the form that posted; both modules apply the same rule.
 */
const POPUP_PENDING_KEY = 'newsletter-popup:pending';

/**
 * @typedef {object} PendingSubmission
 * @property {string} formId - The id of the form that posted.
 * @property {string} variantId - The variant the signup was for.
 * @property {string} tags - The posted `contact[tags]` value.
 * @property {number} time - When the form was sent, in milliseconds since the epoch.
 */

/**
 * @typedef {'success' | 'error' | 'existing'} PostedResult
 * `existing`: Shopify didn't update an email that is already subscribed (no success, no errors).
 */

/**
 * Reads a sessionStorage value. Storage can be unavailable (privacy settings, sandboxing) and throw.
 *
 * @param {string} key - The storage key.
 * @returns {string | null} The stored value, or null when missing or unavailable.
 */
function readSession(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Writes a sessionStorage value, ignoring unavailable or full storage. Without it the URL hash
 * still scopes the result to the form that sent it.
 *
 * @param {string} key - The storage key.
 * @param {string} value - The value to store.
 */
function writeSession(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Nothing is remembered.
  }
}

/**
 * Removes a sessionStorage value, ignoring unavailable storage.
 *
 * @param {string} key - The storage key.
 */
function removeSession(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to remove when storage is unavailable.
  }
}

/**
 * Parses the pending-submission marker.
 *
 * @param {string | null} value - The stored JSON.
 * @returns {PendingSubmission | null} The marker, or null when missing or malformed.
 */
function parsePending(value) {
  if (!value) return null;

  try {
    const data = JSON.parse(value);
    if (typeof data?.formId === 'string' && typeof data?.time === 'number') {
      return {
        formId: data.formId,
        variantId: typeof data.variantId === 'string' ? data.variantId : '',
        tags: typeof data.tags === 'string' ? data.tags : '',
        time: data.time,
      };
    }
  } catch {
    // A malformed marker is treated as missing.
  }

  return null;
}

/**
 * Runs a callback once every parser-inserted module script has run, which happens before
 * DOMContentLoaded. Other modules, such as the newsletter popup, read the posted URL and the marker
 * while they initialize, so both stay in place until then.
 *
 * @param {() => void} callback - The callback.
 */
function afterModulesRun(callback) {
  if (document.readyState === 'complete') {
    queueMicrotask(callback);
    return;
  }

  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    callback();
  };

  // `load` covers a module that runs after DOMContentLoaded has already fired.
  document.addEventListener('DOMContentLoaded', run, { once: true });
  window.addEventListener('load', run, { once: true });
}

/**
 * A "Notify me when available" signup for the selected variant.
 *
 * The block renders on every product page and stays hidden while the selected variant can be
 * bought. Variant changes come from the variant picker's product select event: the block reads its
 * own server-rendered copy from the fetched page and updates its visibility, variant and customer
 * tags, keeping what the shopper typed. The customer form posts the whole page. Every customer form
 * on the returned page renders the shared posted state, so the result shows only in the form that
 * sent it, identified by the URL hash (the form id) or a sessionStorage marker written on submit.
 * The returned page can render another variant than the one posted (a featured product section
 * keeps no `?variant=` in the URL), so the result restores the posted variant from the marker.
 *
 * @typedef {object} BackInStockRefs
 * @property {HTMLInputElement} tags - The hidden `contact[tags]` input.
 * @property {HTMLInputElement} emailInput - The email field.
 * @property {HTMLButtonElement} [submitButton] - The submit button.
 * @property {HTMLElement} [error] - The server-rendered error; rendered after a failed post.
 * @property {HTMLElement} [existing] - The server-rendered already-subscribed notice.
 * @property {HTMLElement} [success] - The server-rendered confirmation; rendered after a successful post.
 * @property {HTMLElement} [successHeading] - The confirmation heading, focused when the result loads.
 *
 * @extends {Component<BackInStockRefs>}
 */
class BackInStockComponent extends Component {
  requiredRefs = ['tags', 'emailInput'];

  /** @type {AbortController | undefined} Listeners that live while the element is connected. */
  #controller;

  /** Whether this page view's posted result was already handled; reconnects don't repeat it. */
  #resultHandled = false;

  /** @type {PostedResult | undefined} The result of a post this form sent. */
  #postedResult;

  /** @type {string} The variant a successful post from this form signed up for. */
  #postedVariantId = '';

  /**
   * @type {Node | undefined} Copy of the confirmation. A featured product's variant change morphs
   * the whole section, and the section response has no posted state, so it drops the original.
   */
  #successCopy;

  /** Counts product select events so an older response never overrides a newer selection. */
  #selectRequest = 0;

  /** Set while the form is being sent, so a double click doesn't post twice. */
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();

    // Quick add shows product details without this block (hidden by its modal styles), and a post
    // from there would return to a page without it.
    if (this.closest('quick-add-dialog')) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });
    window.addEventListener('pageshow', this.#handlePageShow, { signal });

    if (!this.#resultHandled) {
      this.#resultHandled = true;
      this.#handlePostedResult();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = undefined;
    this.#selectRequest += 1;
  }

  /**
   * Remembers that this form sent the customer form, so the returned page shows the result here.
   * The submit event only fires for input that passes validation.
   *
   * @param {SubmitEvent} event - The submit event.
   */
  handleSubmit(event) {
    if (this.#submitting) {
      event.preventDefault();
      return;
    }

    this.#submitting = true;
    this.refs.submitButton?.setAttribute('aria-disabled', 'true');

    /** @type {PendingSubmission} */
    const pending = {
      formId: this.#formId,
      variantId: this.dataset.variantId ?? '',
      tags: this.refs.tags.value,
      time: Date.now(),
    };
    writeSession(PENDING_KEY, JSON.stringify(pending));
  }

  get #formId() {
    return this.refs.tags.form?.id ?? '';
  }

  get #isDesignMode() {
    return this.hasAttribute('data-design-mode');
  }

  /**
   * Shows the result of a post this form sent, and drops a result another customer form's post
   * rendered here.
   */
  #handlePostedResult() {
    const state = this.dataset.formState;
    if (state !== 'success' && state !== 'error' && state !== 'existing') return;

    const pending = this.#getPendingFromThisForm();
    if (!this.#wasSentHere(pending)) {
      this.#setResult('none');

      // Another form posted after this one, so this form's result will never arrive.
      if (pending) {
        afterModulesRun(() => {
          if (this.#getPendingFromThisForm()) removeSession(PENDING_KEY);
        });
      }
      return;
    }

    // A featured product section keeps no ?variant= in the URL, so the returned page can render
    // another variant than the one posted; restore it so the result shows and a resend tags it.
    if (pending?.variantId && pending.variantId !== this.dataset.variantId) {
      this.dataset.variantId = pending.variantId;
      if (pending.tags) {
        this.refs.tags.value = pending.tags;
        this.refs.tags.setAttribute('value', pending.tags);
      }
    }

    this.#postedResult = state;
    this.#postedVariantId = this.dataset.variantId || '';
    this.#setResult(state);

    // The result always shows in the form that sent it, even when the page rendered an available
    // variant (another one, or the posted one back in stock in the meantime).
    this.hidden = false;
    if (state === 'success') this.#successCopy = this.refs.success?.cloneNode(true);

    // Focus continues from the form the shopper sent. With the hash in the URL the browser has
    // already scrolled here; without it (bot-protection redirect) focusing scrolls here.
    const target =
      state === 'success' ? this.refs.successHeading : state === 'existing' ? this.refs.existing : this.refs.emailInput;
    target?.focus();

    afterModulesRun(() => this.#clearPostedState());
  }

  /**
   * Returns the pending marker when this form wrote it and it hasn't expired.
   *
   * @returns {PendingSubmission | null}
   */
  #getPendingFromThisForm() {
    const pending = parsePending(readSession(PENDING_KEY));
    if (!pending || pending.formId !== this.#formId) return null;

    const age = Date.now() - pending.time;
    return age >= 0 && age < PENDING_MAX_AGE_MS ? pending : null;
  }

  /**
   * Whether this form sent the post whose result the page shows. The posting form's id becomes the
   * URL hash; the sessionStorage marker covers redirects that drop it.
   *
   * @param {{ formId: string, time: number } | null} pending - This form's unexpired marker.
   * @returns {boolean}
   */
  #wasSentHere(pending) {
    const id = this.#hashId();
    if (id === this.#formId) return true;

    // Another form's id in the hash names the form that posted.
    if (id && document.getElementById(id) instanceof HTMLFormElement) return false;

    if (!pending) return false;

    // A newer submission from the newsletter popup is the one this page shows.
    let popupTime = NaN;
    try {
      popupTime = JSON.parse(readSession(POPUP_PENDING_KEY) ?? 'null')?.time;
    } catch {
      // A malformed marker is treated as missing.
    }

    return !(typeof popupTime === 'number' && popupTime > pending.time);
  }

  /**
   * @returns {string} The decoded URL hash without `#`, or an empty string.
   */
  #hashId() {
    const hash = window.location.hash.slice(1);

    try {
      return decodeURIComponent(hash);
    } catch {
      return hash;
    }
  }

  /**
   * Removes this form's hash, the `customer_posted` flag Shopify reads to render the posted state,
   * and the marker, so a reload doesn't show the result again.
   */
  #clearPostedState() {
    if (this.#getPendingFromThisForm()) removeSession(PENDING_KEY);

    try {
      const url = new URL(window.location.href);
      const hasFormHash = this.#hashId() === this.#formId;
      const hasPostedFlag = url.searchParams.has('customer_posted');
      if (!hasFormHash && !hasPostedFlag) return;

      if (hasFormHash) url.hash = '';
      if (hasPostedFlag) url.searchParams.delete('customer_posted');
      history.replaceState(history.state, '', url);
    } catch {
      // Leaving the URL in place only means a reload shows the result again.
    }
  }

  /**
   * Applies a result state and keeps the email field's error state in sync with it.
   *
   * @param {'none' | PostedResult} result - The state to show.
   */
  #setResult(result) {
    this.dataset.result = result;

    const { emailInput, error } = this.refs;
    const errorId = error instanceof HTMLElement ? error.id : '';
    const showError = result === 'error' && Boolean(errorId);

    if (showError) {
      emailInput.setAttribute('aria-invalid', 'true');
    } else {
      emailInput.removeAttribute('aria-invalid');
    }

    const describedBy = (emailInput.getAttribute('aria-describedby') ?? '')
      .split(' ')
      .filter((id) => id && id !== errorId);
    if (showError) describedBy.unshift(errorId);

    if (describedBy.length) {
      emailInput.setAttribute('aria-describedby', describedBy.join(' '));
    } else {
      emailInput.removeAttribute('aria-describedby');
    }
  }

  /**
   * Updates the block for the variant the shopper picked, from its copy in the fetched page.
   *
   * @param {Event} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { promise } = /** @type {import('@shopify/events').ProductSelectEvent} */ (event);
    if (typeof promise?.then !== 'function') return;

    const request = ++this.#selectRequest;
    // Read before the variant picker's update, which can morph this block (featured product).
    const typedEmail = this.refs.emailInput.value;

    promise
      .then((result) => {
        if (request !== this.#selectRequest || !this.isConnected) return;

        const detail = result?.detail;
        const html = detail?.html;
        if (!html) return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const next = html.querySelector(`#${CSS.escape(this.id)}`);
        if (next instanceof HTMLElement) this.#applyVariant(next, typedEmail);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[back-in-stock] Event promise rejected:', error);
      });
  };

  /**
   * Copies the variant-specific state from the block's server-rendered copy for the new variant.
   *
   * In a featured product section the variant picker morphs the whole section before this runs,
   * which resets this block to a fresh server render: no posted result markup and the server's
   * email value. The rest of the state is restored here.
   *
   * @param {HTMLElement} next - This block as rendered for the newly selected variant.
   * @param {string} typedEmail - The email field's value when the variant change started.
   */
  #applyVariant(next, typedEmail) {
    const variantId = next.dataset.variantId ?? '';
    this.dataset.variantId = variantId;

    const nextTags = next.querySelector('input[ref="tags"]');
    if (nextTags instanceof HTMLInputElement) {
      this.refs.tags.value = nextTags.value;
      this.refs.tags.setAttribute('value', nextTags.value);
    }

    // A morph puts the server's value back (its value attribute); keep what the shopper typed.
    // A value that differs from the attribute was typed after the change started, so it stays.
    const { emailInput } = this.refs;
    if (emailInput.value !== typedEmail && emailInput.value === emailInput.defaultValue) {
      emailInput.value = typedEmail;
    }

    // A result belongs to the variant it was sent for: an error or already-subscribed notice
    // clears, a confirmation returns when the shopper picks that variant again.
    if (this.#postedResult === 'error' || this.#postedResult === 'existing') {
      this.#postedResult = undefined;
      this.#setResult('none');
    } else if (this.#postedResult === 'success') {
      let showSuccess = variantId === this.#postedVariantId;

      // Put back a confirmation the featured product morph removed, rather than showing an empty
      // box. Queried directly: refs refresh only after the morph's updatedCallback.
      if (showSuccess && !this.querySelector('.back-in-stock__success') && this.#successCopy) {
        this.querySelector('.back-in-stock__form')?.append(this.#successCopy.cloneNode(true));
      }
      showSuccess &&= Boolean(this.querySelector('.back-in-stock__success'));

      this.#setResult(showSuccess ? 'success' : 'none');
    }

    // The editor keeps the block visible so it can be edited.
    if (this.#isDesignMode) return;

    const shouldHide = next.hidden;
    if (shouldHide === this.hidden) return;

    if (!shouldHide) this.toggleAttribute('data-animate', true);
    this.hidden = shouldHide;
  }

  /**
   * A page restored from the back-forward cache keeps the sending state; let the form send again.
   *
   * @param {PageTransitionEvent} event - The pageshow event.
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    this.#submitting = false;
    this.refs.submitButton?.removeAttribute('aria-disabled');
  };
}

if (!customElements.get('back-in-stock-component')) {
  customElements.define('back-in-stock-component', BackInStockComponent);
}
